/*
 * Voyado filter bar + drawer — UI lifecycle only.
 * -----------------------------------------------
 * Adapted from the original assets/plp-filters-drawer.js + plp-filter-bar.js.
 * All Shopify form-submit / Section-Rendering preview
 * logic is removed — data is owned by assets/voyado-plp-controller.js, which
 * listens for the `voyado:drawer-open` / `voyado:drawer-close` events this
 * file dispatches.
 *
 * Owns: drawer open/close (focus trap, scroll lock, Escape, overlay, chip
 * accordion targeting), the sticky-bar observer + --sticky-header-offset,
 * the sort dropdown outside-click close, and the tile/gallery view switch.
 *
 * Depends on: focus-trap.js (global in theme.liquid).
 */
(function () {
  'use strict';

  /* global createFocusTrap */

  const root = document.querySelector('[data-main-collection], [data-main-search]');
  if (!root) return;

  const container = document.querySelector('[data-plp-filters-drawer]');
  const panel = container && container.querySelector('[data-plp-filters-drawer-panel]');
  const overlay = container && container.querySelector('[data-plp-filters-drawer-overlay]');
  const trap = (panel && typeof createFocusTrap === 'function') ? createFocusTrap(panel) : null;

  const abortController = new AbortController();
  const { signal } = abortController;
  let lastTrigger = null;

  // --- Drawer open / close -------------------------------------------------

  // Chip triggers carry data-plp-filter-target=<facet.id>: open only the
  // matching accordion. "All Filters" (no target) opens every accordion.
  function applyAccordionState(targetParam) {
    if (!panel) return;
    panel.querySelectorAll('details[data-accordion-item][data-filter-param]').forEach(function (acc) {
      acc.open = !targetParam || acc.dataset.filterParam === targetParam;
    });
  }

  function setExpandedIfOwner(el, value) {
    if (el && el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', value);
  }

  function openDrawer(trigger) {
    if (!container) return;
    lastTrigger = trigger || null;
    container.setAttribute('data-plp-filters-drawer-open', 'true');
    if (panel) { panel.removeAttribute('aria-hidden'); panel.removeAttribute('inert'); }
    const sc = container.querySelector('[data-drawer-scroll]'); if (sc) sc.scrollTop = 0; // open from the top
    setExpandedIfOwner(trigger, 'true');
    document.documentElement.setAttribute('data-scroll-lock', '');
    // The controller re-renders the facet rows on this event (resync to committed),
    // so apply the chip-target accordion state AFTER, on the freshly rendered rows,
    // then move focus onto the final layout.
    document.dispatchEvent(new CustomEvent('voyado:drawer-open', { bubbles: true }));
    applyAccordionState(trigger ? trigger.dataset.plpFilterTarget : null);
    if (trap) { trap.activate(); trap.focusFirst(); }
  }

  function closeDrawer() {
    if (!container) return;
    container.setAttribute('data-plp-filters-drawer-open', 'false');
    if (panel) { panel.setAttribute('aria-hidden', 'true'); panel.setAttribute('inert', ''); }
    document.documentElement.removeAttribute('data-scroll-lock');
    if (trap) trap.deactivate();
    if (lastTrigger) {
      setExpandedIfOwner(lastTrigger, 'false');
      if (typeof lastTrigger.focus === 'function') lastTrigger.focus();
    }
    lastTrigger = null;
    document.dispatchEvent(new CustomEvent('voyado:drawer-close', { bubbles: true }));
  }

  function isOpen() {
    return container && container.getAttribute('data-plp-filters-drawer-open') === 'true';
  }

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-plp-filter-bar-filters]');
    if (trigger) { e.preventDefault(); openDrawer(trigger); return; }
    // Apply closes the drawer; the controller commits the filter data.
    const apply = e.target.closest('[data-plp-filters-drawer-apply]');
    if (apply && container && container.contains(apply)) { closeDrawer(); return; }
    const closeBtn = e.target.closest('[data-plp-filters-drawer-close]');
    if (closeBtn && container && container.contains(closeBtn)) { closeDrawer(); return; }
  }, { signal });

  if (overlay) {
    overlay.addEventListener('click', function () { if (isOpen()) closeDrawer(); }, { signal });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) closeDrawer();
  }, { signal });

  // --- Sort dropdown outside-click close -----------------------------------

  const sortRoot = root.querySelector('[data-plp-filter-bar-sort-by]');
  if (sortRoot) {
    document.addEventListener('click', function (e) {
      if (sortRoot.open && !sortRoot.contains(e.target)) sortRoot.open = false;
    }, { signal });
  }

  // --- Grid view switcher (tile / gallery) ---------------------------------

  const switcher = root.querySelector('[data-plp-filter-bar-grid-switcher]');
  const grid = root.querySelector('[data-voyado-grid]');
  if (switcher && grid) {
    const links = switcher.querySelectorAll('[data-grid-switcher-option]');
    const applyView = function (view) {
      grid.dataset.view = view;
      links.forEach(function (link) {
        if (link.dataset.gridSwitcherOption === view) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const url = new URL(window.location.href);
      if (view === 'tile') url.searchParams.delete('view');
      else url.searchParams.set('view', view);
      history.replaceState({}, '', url.toString());
    };
    const initial = new URL(window.location.href).searchParams.get('view');
    if (initial === 'gallery') applyView('gallery');
    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        const next = link.dataset.gridSwitcherOption;
        if (next === 'tile' || next === 'gallery') applyView(next);
      }, { signal });
    });
  }

  // --- Sticky bar offset + stuck observer ----------------------------------

  const header = document.querySelector('[data-header]');
  // Sticky state lives on the header GROUP (announcement bar + header), not [data-header].
  const group = document.querySelector('[data-header-group]') || header;
  let headerMO = null;
  let headerRO = null;
  if (header) {
    const updateOffset = function () {
      const visible = group.dataset.headerSticky === 'visible';
      document.documentElement.style.setProperty(
        '--sticky-header-offset',
        visible ? header.getBoundingClientRect().height + 'px' : '0px'
      );
    };
    updateOffset();
    headerMO = new MutationObserver(updateOffset);
    headerMO.observe(group, { attributes: true, attributeFilter: ['data-header-sticky'] });
    if (typeof ResizeObserver === 'function') {
      headerRO = new ResizeObserver(updateOffset);
      headerRO.observe(header);
    }
  }

  const sentinel = root.querySelector('[data-plp-filter-bar-sentinel]');
  const stickyWrap = root.querySelector('[data-plp-filter-bar-sticky]');
  let stickyObserver = null;
  if (sentinel && stickyWrap && typeof IntersectionObserver === 'function') {
    stickyObserver = new IntersectionObserver(function (entries) {
      const entry = entries[0];
      const isStuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      const desired = isStuck ? 'true' : 'false';
      if (stickyWrap.dataset.stuck === desired) return;
      stickyWrap.style.transitionProperty = 'none';
      stickyWrap.dataset.stuck = desired;
      stickyWrap.offsetHeight; // force reflow before restoring transition
      stickyWrap.style.transitionProperty = '';
    });
    stickyObserver.observe(sentinel);
  }

  // --- Cleanup -------------------------------------------------------------

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.contains(root)) {
      if (stickyObserver) stickyObserver.disconnect();
      if (headerMO) headerMO.disconnect();
      if (headerRO) headerRO.disconnect();
      abortController.abort();
    }
  }, { once: true });
})();
